import { type FC, type ReactNode, memo, useEffect, useRef, useState } from "react";
import { DataTestIds } from "src/shared/constants/TestEnums";

export interface ViewportWrapperProps {
  /** Render function - component is only created when in viewport */
  renderContent: () => ReactNode;
  skeleton?: ReactNode;
  rootMargin?: string;
}

/**
 * Defers mounting children until the wrapper enters the viewport.
 * Shows a skeleton placeholder until visible.
 *
 * Uses a render function prop (`renderContent`) instead of `children` to ensure
 * child components are only instantiated (and their hooks/API calls only execute)
 * when the wrapper enters the viewport — not before.
 */
export const ViewportWrapper: FC<ViewportWrapperProps> = memo(
  ({ renderContent, skeleton = null, rootMargin = "200px" }) => {
    const [hasEnteredView, setHasEnteredView] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const element = ref.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) {
            return;
          }

          setHasEnteredView(true);
          observer.disconnect();
        },
        { rootMargin },
      );

      observer.observe(element);
      return () => {
        observer.disconnect();
      };
    }, [rootMargin]);

    return (
      <div ref={ref} data-testid={DataTestIds.ViewportWrapper}>
        {hasEnteredView ? renderContent() : skeleton}
      </div>
    );
  },
);

ViewportWrapper.displayName = "ViewportWrapper";
